import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { Box, Grid } from '@material-ui/core';
import { camelCase, isEmpty, pull, last } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useHistory } from 'react-router-dom';

import { ButtonComponent, CardComponent, PageHeader, PageHeaderNav } from 'components';
import { UnlockDescriptionDialog } from 'components/Dialogs';
import { AutocompleteValidatorComponent } from 'components/FormComponents';
import { PROMISE_STATUSES, DESCRIPTION_DIALOG_STORAGE_KEY } from 'constants/technical';
import { useAuthState } from 'contexts/AuthContext';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { useCategoriesState, useCategoriesActions } from 'contexts/CategoriesContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { htmlTemplatesModel, picturesModel, productModel, productVariantsModel } from 'models';
import { ROUTES } from 'routes/constants';
import { getDefaultMultilanguageField, getFormattedDate, getServerError, getTWithPathKey } from 'utils';
import { withPageLanguage, withPageLanguageDefaultProps, withPageLanguagePropTypes } from 'utils/enhancers';

import { PICTURE_TYPES_FILTER, UPDATE_VARIANT_IMAGES_STATUS } from './constants';
import { ProductField } from './ProductField/ProductField';
import { ProductPictures } from './ProductPictures/ProductPictures';
import { ProductSaveConfirmation } from './ProductSaveConfirmation/ProductSaveConfirmation';
import ProductTheme from './ProductTheme';
import { ProductVariantsTable } from './ProductVariantsTable/ProductVariantsTable';
import { ProductVideos } from './ProductVideos/ProductVideos';
import { defaultReleaseCountdown, ReleaseCountdown } from './ReleaseCountdown/ReleaseCountdown';

function ManageProduct({ id, title, pageLanguageTabValue, handleValidationCheck, children }) {
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [previewImagesToDelete, setPreviewImagesToDelete] = useState([]);
  const [product, setProduct] = useState({});
  const [additionalFields, setAdditionalFields] = useState([]);
  const [mainFields, setMainFields] = useState([]);
  const [categoryField, setCategoryField] = useState({});
  const [productPictures, setProductPictures] = useState([]);
  const [productPreviewPictures, setProductPreviewPictures] = useState([]);
  const [productPreviewPicturesEnabled, setProductPreviewPicturesEnabled] = useState(false);
  const [productVideos, setProductVideos] = useState([]);
  const [productReleaseCountdown, setProductReleaseCountdown] = useState(defaultReleaseCountdown);
  const [withCountdown, setWithCountdown] = useState(false);
  const [updatedVariantImages, setUpdatedVariantImages] = useState({});
  const [pdfStatuses, setPdfStatuses] = useState([]);
  const [htmlTemplates, setHtmlTemplates] = useState([]);
  const [selectedHtmlTemplate, setSelectedHtmlTemplate] = useState(null);
  const [isUnlockDialogOpen, setUnlockDialogOpen] = useState(false);

  const history = useHistory();
  const { handleServerError } = useServerErrorActions();
  const {
    currentUser: {
      attributes: { clientLanguages, shoppingPlatformName, productImagesRequired },
    },
  } = useAuthState();
  const { withPageProgressHandler } = useBusyProgressActions();
  const { categories } = useCategoriesState();
  const { getCategoriesList } = useCategoriesActions();
  const getErrorsT = getTWithPathKey('common.errors');
  const getT = getTWithPathKey('pages.products.manageProduct');

  useEffect(() => {
    if (!categoryField.readonly) {
      getCategoriesList();
    }
  }, []);

  useEffect(() => {
    getProduct();
    getAttributes();
    getTemplates();
  }, [id]);

  useEffect(() => {
    if (!id && mainFields.length) {
      setProduct(prev => ({
        ...prev,
        ...getDefaultMultilanguageField(mainFields, clientLanguages),
        ...getDefaultMultilanguageField(additionalFields, clientLanguages),
      }));
    }
  }, [mainFields, additionalFields]);

  const uploadedVariantsPictures = useMemo(
    () => productPictures.filter(picture => PICTURE_TYPES_FILTER.uploaded(picture)),
    [productPictures],
  );

  const variantsWithUpdatedPictures = useMemo(
    () =>
      product.variants?.map(variant => ({
        ...variant,
        picture: (updatedVariantImages[variant.id] || variant).picture,
      })),
    [product.variants, updatedVariantImages],
  );

  const hasProduct = useMemo(() => !isEmpty(product), [product]);

  const getAttributes = withPageProgressHandler(() =>
    productModel
      .getProductAttributes()
      .then(data => {
        const allMainFields = getFieldsWithCamelCaseNames(data.mainFields);

        setAdditionalFields(
          getFieldsWithCamelCaseNames(data.additionalFields).filter(
            i => !['htmlTemplateId', 'descriptionLocked'].includes(i.name),
          ),
        );
        if (allMainFields.length !== 0) {
          setMainFields(allMainFields.filter(({ name }) => name !== 'categoryId'));
          setCategoryField(allMainFields.find(({ name }) => name === 'categoryId'));
        }
      })
      .catch(handleServerError)
  );

  const getTemplates = withPageProgressHandler(() =>
    htmlTemplatesModel
      .getTemplates()
      .then(data => {
        setHtmlTemplates(data);
      })
      .catch(handleServerError)
  );

  const getFieldsWithCamelCaseNames = fields => fields.map(({ name, ...rest }) => ({ ...rest, name: camelCase(name) }));

  const initializeDataWithDataFromServer = data => {
    const {
      picturedPdfDocuments,
      previewPictures = [],
      previewPicturesEnabled,
      pictures = [],
      productVideos: videos = [],
      releaseCountdown = {},
      htmlTemplateId,
      ...rest
    } = data;

    setSelectedHtmlTemplate(htmlTemplateId);
    setProduct(prevProduct => ({ ...prevProduct, ...rest }));
    setProductPictures(pictures);
    setPdfStatuses(picturedPdfDocuments?.filter(i => !!i.status && i.status !== 'error') || []);
    setProductPreviewPictures(previewPictures);
    setProductPreviewPicturesEnabled(previewPicturesEnabled);
    setProductVideos(videos);

    if (!isEmpty(releaseCountdown)) {
      setProductReleaseCountdown({
        ...releaseCountdown,
        releaseTime: getFormattedDate(releaseCountdown.releaseTime),
      });
      setWithCountdown(releaseCountdown.preRelease);
    }
  };

  const getProduct = withPageProgressHandler(() => {
    if (id) {
      return productModel.getProduct(id).then(initializeDataWithDataFromServer).catch(handleServerError);
    }

    return productModel.getNewProduct().then(initializeDataWithDataFromServer).catch(handleServerError);
  });

  const getFields = fields => fields.reduce((acc, { name }) => ({ ...acc, [name]: product[name] }), {});

  const handleSubmit = withPageProgressHandler(async () => {
    const { videoFirst, descriptionLocked } = product;
    const htmlTemplate = shoppingPlatformName ? { htmlTemplateId: selectedHtmlTemplate } : {};

    const submitData = {
      ...getFields([...mainFields, categoryField]),
      pictures: productPictures,
      previewPictures: productPreviewPictures,
      previewPicturesEnabled: productPreviewPicturesEnabled,
      productVideos,
      videoFirst,
      productInfo: {
        ...getFields(additionalFields),
        ...htmlTemplate,
        descriptionLocked,
      },
      releaseCountdown: productReleaseCountdown.preRelease
        ? {
            ...productReleaseCountdown,
            releaseTime: moment.utc(productReleaseCountdown.releaseTime),
          }
        : null,
    };

    const currentPictures = productPictures.filter(i => !imagesToDelete.includes(i.id));

    if (productImagesRequired && !currentPictures.length) {
      handleServerError(getServerError(getErrorsT('noBlankPictures')));
      return;
    }

    const isGalleryImagesExists = currentPictures.some(i => i.isGallery);

    if (productImagesRequired && !isGalleryImagesExists) {
      handleServerError(getServerError(getErrorsT('atLeastOneGalleryImage')));
      return;
    }

    const isPreviewPicturesExists = !!productPreviewPictures.filter(i => !previewImagesToDelete.includes(i.id)).length;
    const isPreviewPictureError = productPreviewPicturesEnabled && !isPreviewPicturesExists;

    if (isPreviewPictureError) {
      handleServerError(getServerError(getErrorsT('noBlankPreviews')));
      return;
    }

    if (id) {
      const deletedImageIds = [];

      try {
        const deleteImagesArray = [...previewImagesToDelete, ...imagesToDelete].map(picturesModel.deletePicture);

        await Promise.allSettled(deleteImagesArray).then(res => {
          res
            .sort(({ status }) => (status === PROMISE_STATUSES.FULFILLED ? -1 : 1)) // apply sorting to handle success cases first
            .forEach(({ status, reason, value }) => {
              const picId = parseInt(last((reason || value).config.url.split('/')), 10);

              deletedImageIds.push(picId); // should keep success id's and remove later

              if (status === PROMISE_STATUSES.REJECTED) {
                throw reason;
              } else {
                // drop image from updating
                const picturesWithoutDeleted = submitData.pictures.filter(({ id }) => picId !== id);
                const previewWithoutDeleted = submitData.previewPictures.filter(({ id }) => picId !== id);

                setProductPictures(picturesWithoutDeleted);
                setProductPreviewPictures(previewWithoutDeleted);
                submitData.pictures = picturesWithoutDeleted;
                submitData.previewPictures = previewWithoutDeleted;
              }
            });
        });
        await productModel.editProduct(id, submitData);

        const updateVariantImagesIDs = Object.keys(updatedVariantImages);

        if (updateVariantImagesIDs.length) {
          await Promise.all(
            updateVariantImagesIDs.map(variantId => {
              const { status, picture } = updatedVariantImages[variantId];

              return status === UPDATE_VARIANT_IMAGES_STATUS.upload
                ? productVariantsModel.updatePicture(variantId, picture.id)
                : productVariantsModel.removePicture(variantId);
            }),
          );
        }

        await handleUseOnSphere();

        handleRedirectToProducts();
      } catch (e) {
        setImagesToDelete(pull(imagesToDelete, ...deletedImageIds)); // cleanup array of id's in case user will resubmit
        setPreviewImagesToDelete(pull(previewImagesToDelete, ...deletedImageIds));
        handleServerError(e);
      }
    } else {
      try {
        await productModel.addProduct(submitData);

        await handleUseOnSphere();
        handleRedirectToProducts();
      } catch (e) {
        handleServerError(e);
      }
    }
  });

  const handleUseOnSphere = () => {
    const ternedOnUseOnSpherePictures = productPictures
      .filter(omitDeletedPic)
      .filter(({ shouldUsedOnPlanogram }) => shouldUsedOnPlanogram)
      .map(({ id }) => ({ id, usedOnPlanogram: true }));

    const ternedOffUsedOnSpherePictures = productPictures
      .filter(omitDeletedPic)
      .filter(({ usedOnPlanogram, shouldUsedOnPlanogram }) => !usedOnPlanogram && !shouldUsedOnPlanogram)
      .map(({ id }) => ({ id, usedOnPlanogram: false }));

    const imagesForBulkUpdate = [...ternedOnUseOnSpherePictures, ...ternedOffUsedOnSpherePictures];

    return isEmpty(imagesForBulkUpdate) ? Promise.resolve() : picturesModel.useImagesOnSphere(imagesForBulkUpdate);
  };

  const handleChangeProduct = useCallback(item => setProduct(prevState => ({ ...prevState, ...item })), []);

  const handleRedirectToProducts = () => history.push(ROUTES.manageProducts);

  const withConfirmation = () => withCountdown && !productReleaseCountdown.preRelease;
  const omitDeletedPic = pic => !imagesToDelete.includes(pic.id);
  const omitDeletedPreview = pic => !previewImagesToDelete.includes(pic.id);

  const hasPictureVariant = (pictureId, updatedVariantImages) =>
    product.variants.some(variant => {
      const picture = updatedVariantImages[variant.id] ? updatedVariantImages[variant.id].picture : variant.picture;

      return picture?.id === pictureId;
    });

  const updateVariantPictures = (updatedVariant, picture) => {
    const newUpdatedVariantImages = {
      ...updatedVariantImages,
      ...updatedVariant,
    };

    setUpdatedVariantImages(newUpdatedVariantImages);
    setProductPictures(prev =>
      prev.map(prevPicture =>
        picture.id === prevPicture.id
          ? {
              ...prevPicture,
              hasVariant: hasPictureVariant(picture.id, newUpdatedVariantImages),
            }
          : prevPicture,
      ),
    );
  };

  const setVariantImageToUpload = (variantId, picture) =>
    picture &&
    updateVariantPictures(
      {
        [variantId]: {
          status: UPDATE_VARIANT_IMAGES_STATUS.upload,
          picture,
        },
      },
      picture,
    );

  const setVariantImageToRemove = (variantId, picture) =>
    updateVariantPictures(
      {
        [variantId]: {
          status: UPDATE_VARIANT_IMAGES_STATUS.remove,
          picture: null,
        },
      },
      picture,
    );

  const removePreviewImage = imageId => {
    const currentImage = productPreviewPictures.find(i => i.id === imageId);

    if (!currentImage?.created) {
      setPreviewImagesToDelete(prevState => [...prevState, imageId]);
    } else {
      setProductPreviewPictures(prevState => prevState.filter(i => i.id !== imageId));
    }
  };

  const removeImage = imageId => setImagesToDelete(prevState => [...prevState, imageId]);

  const handleTemplateChanges = async (id, fields) => {
    await htmlTemplatesModel.updateTemplate(id, fields);
    await getTemplates();
  };

  const isShopifyDescription = field => field.name === 'description' && !!shoppingPlatformName;

  const getShopifyDescriptionField = useCallback(
    field => ({
      ...field,
      readonly: product.descriptionLocked,
    }),
    [product.descriptionLocked],
  );

  const editDescButtonText = useMemo(
    () => (product.descriptionLocked ? getT('shopifyDescription.unlock') : getT('shopifyDescription.lock')),
    [product.descriptionLocked],
  );

  const handleDialogClose = () => setUnlockDialogOpen(false);
  const handleDialogSave = hide => {
    if (hide) {
      localStorage.setItem(DESCRIPTION_DIALOG_STORAGE_KEY, 'true');
    }

    setProduct(prevState => ({
      ...prevState,
      descriptionLocked: false,
    }));
  };

  const handleUnlock = useCallback(() => {
    const hideDialog = localStorage.getItem(DESCRIPTION_DIALOG_STORAGE_KEY);

    if (!hideDialog) {
      setUnlockDialogOpen(true);
    } else {
      handleDialogSave();
    }
  }, [setUnlockDialogOpen]);

  const handleLock = useCallback(() => {
    setProduct(prevState => ({
      ...prevState,
      descriptionLocked: true,
    }));
  }, [setProduct]);

  const handleLockState = useMemo(
    () => (product.descriptionLocked ? handleUnlock : handleLock),
    [product.descriptionLocked],
  );

  return (
    <>
      <ValidatorForm onSubmit={handleSubmit}>
        <PageHeader title={title} backLink={ROUTES.manageProducts}>
          <PageHeaderNav withSubmit={!withConfirmation()}>
            <ButtonComponent text="Cancel" onClick={handleRedirectToProducts} />
            {withConfirmation() && <ProductSaveConfirmation handleSubmit={handleSubmit} />}
          </PageHeaderNav>
        </PageHeader>
        {children}
        <Grid container spacing={6}>
          <Grid item xs={12} sm={8}>
            {hasProduct && (
              <>
                {!!shoppingPlatformName && (
                  <ProductTheme
                    selectedHtmlTemplate={selectedHtmlTemplate}
                    htmlTemplates={htmlTemplates}
                    setSelectedTemplate={setSelectedHtmlTemplate}
                    onTemplateChange={handleTemplateChanges}
                  />
                )}
                <CardComponent title="PRODUCT INFO" sm={12}>
                  {mainFields.map((field, i) =>
                    isShopifyDescription(field) ? (
                      <Box display="flex" flexWrap="wrap" key={field.name}>
                        <ButtonComponent
                          className="shopify-description"
                          text={editDescButtonText}
                          onClick={handleLockState}
                        />
                        <ProductField
                          field={getShopifyDescriptionField(field)}
                          value={product[field.name]}
                          handleChangeProduct={handleChangeProduct}
                          handleServerError={handleServerError}
                          pageLanguageTabValue={pageLanguageTabValue}
                          handleValidationCheck={handleValidationCheck}
                          additionalClassName="shopify-description"
                          autoFocus={i === 0}
                        />
                      </Box>
                    ) : (
                      <ProductField
                        key={field.name}
                        field={field}
                        value={product[field.name]}
                        handleChangeProduct={handleChangeProduct}
                        handleServerError={handleServerError}
                        pageLanguageTabValue={pageLanguageTabValue}
                        handleValidationCheck={handleValidationCheck}
                        autoFocus={i === 0}
                      />
                    ),
                  )}
                </CardComponent>
                <CardComponent title="ADDITIONAL" sm={12}>
                  {additionalFields.map(field => (
                    <ProductField
                      key={field.name}
                      field={field}
                      value={product[field.name]}
                      handleChangeProduct={handleChangeProduct}
                      handleServerError={handleServerError}
                      pageLanguageTabValue={pageLanguageTabValue}
                      handleValidationCheck={handleValidationCheck}
                    />
                  ))}
                </CardComponent>
                {shoppingPlatformName && variantsWithUpdatedPictures?.length && (
                  <ProductVariantsTable
                    variants={variantsWithUpdatedPictures}
                    columns={product.variantColumns}
                    defaultPicture={product.defaultPicture}
                    productPictures={uploadedVariantsPictures}
                    uploadVariantImage={setVariantImageToUpload}
                    removeVariantImage={setVariantImageToRemove}
                  />
                )}
                <ProductPictures
                  key="pictures"
                  id={id}
                  pictures={productPictures.filter(omitDeletedPic)}
                  setPictures={setProductPictures}
                  setImagesToDelete={removeImage}
                  handleServerError={handleServerError}
                  withFilterTabs
                  withUseOnSphere
                  withShoppingPlatformCheck
                  withHeaderText
                  readonly={product.mediaReadonly}
                  title={getT('productPictures.title')}
                  sectionType="productPictures"
                  columns={product.variantColumns}
                  variants={variantsWithUpdatedPictures}
                  withAddToGallery
                />
                <ProductPictures
                  key="productPreview"
                  id={id}
                  pictures={productPreviewPictures.filter(omitDeletedPreview)}
                  setPictures={setProductPreviewPictures}
                  setImagesToDelete={removePreviewImage}
                  handleServerError={handleServerError}
                  withPdfLoader
                  withBlockSwitcher
                  withLoader
                  withAllPicturesRemove
                  isSwitcherEnabled={productPreviewPicturesEnabled}
                  handleSwitcherChange={setProductPreviewPicturesEnabled}
                  title={getT('productPreview.title')}
                  loadingText={getT('productPreview.loadingText')}
                  sectionType="productPreview"
                  unloadImageErrorText={getErrorsT('noBlankPreviews')}
                  pdfStatuses={pdfStatuses}
                  updatePdfStatuses={setPdfStatuses}
                />
                <ReleaseCountdown
                  productReleaseCountdown={productReleaseCountdown}
                  setProductReleaseCountdown={setProductReleaseCountdown}
                />
                <ProductVideos
                  productVideos={productVideos}
                  setProductVideos={setProductVideos}
                  videoFirst={Boolean(product.videoFirst)}
                  setVideoFirst={handleChangeProduct}
                  readonly={product.mediaReadonly}
                />
              </>
            )}
          </Grid>
          <Grid item sm={4}>
            <CardComponent title="CATEGORY" sm={12}>
              <AutocompleteValidatorComponent
                value={product?.categoryId || ''}
                fieldName="categoryId"
                handleChange={handleChangeProduct}
                selectList={categories}
                itemsNamePath="nameWithPrefix"
                label="Category"
                disabled={categoryField.readonly}
              />
            </CardComponent>
          </Grid>
        </Grid>
      </ValidatorForm>
      {isUnlockDialogOpen && (
        <UnlockDescriptionDialog open={isUnlockDialogOpen} onSave={handleDialogSave} onClose={handleDialogClose} />
      )}
    </>
  );
}

ManageProduct.propTypes = {
  ...withPageLanguagePropTypes,
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
};

ManageProduct.defaultProps = {
  ...withPageLanguageDefaultProps,
  id: null,
};

export default withPageLanguage(ManageProduct);
